// packages/console/src/App.tsx
//
// Top-level shell. Gates the console on the FT-6a session: while the refresh
// exchange and identity load are in flight it shows a booting state; a hard 401
// shows a sign-in prompt that navigates to the OIDC login endpoint; a load
// error shows the error (never a fabricated console). Once ready, it renders
// the file transcoder wired to the live session.

import { useState } from "react";
import { useSession } from "./hooks/useSession";
import { FileTranscoder } from "./components/FileTranscoder";
import { LiveTranscoder } from "./components/LiveTranscoder";
import { LivePlaybackPage } from "./components/LivePlaybackPage";
import { LiveWebRTCPlaybackPage } from "./components/LiveWebRTCPlaybackPage";
import { playbackUrlFromLocation, whepUrlFromLocation } from "./lib/livePlayback";
import type { ConsoleMode } from "./components/ModeToggle";
import { Mark } from "./components/Icons";
import { PasswordLoginForm } from "./components/PasswordLoginForm";

export function App() {
  // Standalone live-playback "route": a new tab opened by the live console's Play
  // button carries a "?play=<master url>" query. The minted URL is self-
  // authenticating, so this branch renders the player WITHOUT the session gate and
  // needs no in-memory token. It is a distinct subtree (ConsoleApp), so the
  // session hook only runs in the console, never in the standalone player tab
  // (no conditional hook call). Evaluated once at mount; the tab never navigates.
  if (whepUrlFromLocation(window.location.search)) {
    return <LiveWebRTCPlaybackPage />;
  }
  if (playbackUrlFromLocation(window.location.search)) {
    return <LivePlaybackPage />;
  }
  return <ConsoleApp />;
}

function ConsoleApp() {
  const session = useSession();
  // Top-level console mode. The file/live toggle in the shared top bar flips it
  // and the shell swaps the transcoder in place (no router): file mode is the
  // FileTranscoder unchanged, live mode is the LiveTranscoder.
  const [mode, setMode] = useState<ConsoleMode>("file");

  if (session.phase === "ready" && session.me) {
    return mode === "live" ? (
      <LiveTranscoder session={session} mode={mode} onMode={setMode} />
    ) : (
      <FileTranscoder session={session} mode={mode} onMode={setMode} />
    );
  }

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        background: "var(--bg-base)",
        color: "var(--fg1)",
      }}
    >
      <Mark size={40} />
      <span style={{ font: "600 13px var(--font-sans)", letterSpacing: "var(--ls-wordmark)", color: "#fff" }}>
        AETHER CLOUD
      </span>
      {session.phase === "booting" && (
        <span style={{ font: "var(--t-body-sm)", color: "var(--fg3)" }}>Loading session...</span>
      )}
      {session.phase === "unauthenticated" && (
        <>
          <span style={{ font: "var(--t-body-sm)", color: "var(--fg3)" }}>Sign in to continue.</span>
          <button
            className="ae-b"
            onClick={session.goToLogin}
            style={{
              font: "var(--t-btn)",
              letterSpacing: "var(--ls-btn)",
              textTransform: "uppercase",
              color: "#fff",
              background: "var(--blue-500)",
              border: "1px solid transparent",
              borderRadius: "var(--r-sm)",
              padding: "10px 18px",
              cursor: "pointer",
            }}
          >
            Sign in with Google
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 10, width: 264, color: "var(--fg4)" }}>
            <div style={{ flex: 1, height: 1, background: "var(--line)" }} />
            <span style={{ font: "var(--t-micro)" }}>OR</span>
            <div style={{ flex: 1, height: 1, background: "var(--line)" }} />
          </div>
          <PasswordLoginForm onSuccess={session.refresh} />
        </>
      )}
      {session.phase === "error" && (
        <span style={{ font: "var(--t-body-sm)", color: "var(--err)" }}>
          {session.errorMessage ?? "Session failed to load."}
        </span>
      )}
    </div>
  );
}
